import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import { Spinner } from "src/components";
import { ordActions } from "src/context/order-slice";
import { RootState } from "src/context/store";
import { useToast } from "src/hooks/useToast";
import { getCounterByEmployee, getCounterOrderProductsById } from "src/services/order-api";
import cross from "../../assets/cross.svg";
import PurpleLeftArrowIcon from "../../assets/purple-left-arrow.svg";
import Button from "../../components/Button";
import Input from "src/components/ui/input.component";

const OrdersProductScreen = () => {
  const { locationCode } = useParams();
  // const locationCodeFixed = "141439";
  const { productsData, cartDataObj, selectedCounterData,selectedModule } = useSelector((state: RootState) => state.ord); //M10-12866
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const counterCode = window.sessionStorage.getItem("counterOrderId");
  const [isLoading, setIsLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [filteredProducts, setFilteredProducts] = useState<any[]>([]);

  const { toast } = useToast();

  // M10-12907
  const getSchemeStepAndMax = (item: any) => {
    const slab = item?.counter_product_slab?.[0];
    const priceIdentifierId = String(item?.priceldentifier?.priceldentifier_id ?? "");
    const schemeqty_cp = Number(slab?.schemeqty_cp || 0); // purchase quantity multiple
    const freeqty_cp = Number(slab?.quantity_cp || 0); // free quantity per scheme block
    const max_order_qty = Number(slab?.max_order_qty || 0);
    const min_order_qty = Number(slab?.min_order_qty || 0);
    const integral_quantity = Number(item?.companyProducts?.integral_quantity || 0); // pack size for Discount On PTR / Fixed Price

    let step = 1;
    if (priceIdentifierId === "2" && schemeqty_cp > 0) {
      step = schemeqty_cp;
    } else if ((priceIdentifierId === "1" || priceIdentifierId === "3") && integral_quantity > 0) {
      step = integral_quantity;
    }

    const paidMaxQty = step > 0 ? Math.floor(max_order_qty / step) * step : max_order_qty;
    const paidMinQty = step > 0 ? Math.ceil(min_order_qty / step) * step : min_order_qty;
    const maxFreeQty = step > 0 ? (paidMaxQty / step) * freeqty_cp : 0;

    return { step, max_order_qty, min_order_qty, paidMaxQty, paidMinQty, maxFreeQty };
  };

  const handleAddToCart = (item: any) => {
    const counter_product_lnk_id = item.counter_product_lnk_id;

    const existingItem = cartDataObj?.[counter_product_lnk_id];
    const { step, paidMaxQty, paidMinQty } = getSchemeStepAndMax(item); // M10-12907
    // M10-12907
    const currentQty = Number(existingItem?.cartQty || paidMinQty);
    let newQty = step > 0 ? Math.ceil(currentQty / step) * step : currentQty;
    if (newQty > paidMaxQty) {
      newQty = paidMaxQty;
    }
    if (newQty < paidMinQty) {
      newQty = paidMinQty;
    }
    // M10-12907

    dispatch(
      ordActions.setCartDataObj({
        key: counter_product_lnk_id,
        value: {
          ...item,
          cartQty: newQty,
        },
      })
    );
  };

  const handleIncrement = (counter_product_lnk_id: string) => {
    const currentProd = cartDataObj?.[counter_product_lnk_id];
    const currentQty = currentProd?.cartQty || 0;
    const { step, paidMaxQty } = getSchemeStepAndMax(currentProd); // M10-12907
    const newQty = Number(currentQty) + step;
    if (newQty > paidMaxQty) {
      toast({ description: `Maximum order quantity is ${paidMaxQty}`, variant: "destructive" });
      return;
    } else {
      dispatch(
        ordActions.setCartDataObj({
          key: counter_product_lnk_id,
          value: {
            ...currentProd,
            cartQty: newQty,
          },
        })
      );
    }
  };

  const handleReviewOrder = () => {
    // validation for cart data
    const cartData = Object.values(cartDataObj);
    const isValid = cartData.every((item: any) => {
      const { step, paidMaxQty, paidMinQty } = getSchemeStepAndMax(item); // M10-12907
      const qty = Number(item?.cartQty);
      return qty >= paidMinQty && qty <= paidMaxQty && (step <= 1 || (qty - paidMinQty) % step === 0);
    });
    if (!isValid) {
      toast({
        description: "Please enter a valid quantity",
        variant: "destructive",
      });
      return;
    }

    if (!Object.keys(cartDataObj)?.length) {
      toast({
        description: "Please select at least one product",
      });
      return;
    }
    navigate(`/transactions/orders/review/${locationCode}`);
  };

  const handleDecrement = (counter_product_lnk_id: string) => {
    const currentProd = cartDataObj?.[counter_product_lnk_id];
    const currentQty = currentProd?.cartQty || 0;
    const { step, paidMinQty } = getSchemeStepAndMax(currentProd); // M10-12907
    const newQty = Number(currentQty) - step;
    if (newQty < paidMinQty) {
      dispatch(ordActions.removeCartDataObj({ key: counter_product_lnk_id }));
    } else {
      dispatch(
        ordActions.setCartDataObj({
          key: counter_product_lnk_id,
          value: {
            ...currentProd,
            cartQty: newQty,
          },
        })
      );
    }
  };

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    setSearchTerm(value);

    if (!value) {
      // If search is empty, reset to show all products
      setFilteredProducts([]);
      return;
    }

    // Perform case-insensitive search across multiple fields
    const searchResults = productsData?.filter(
      (item: any) =>
        item?.companyProducts?.item_code?.toLowerCase().includes(value.toLowerCase()) ||
        item?.companyProducts?.name?.toLowerCase().includes(value.toLowerCase()) ||
        item?.counter_ff_div_id?.company_division_code?.division_name?.toLowerCase().includes(value.toLowerCase()) ||
        item?.companyProducts?.ptr?.toString().includes(value)
    );

    setFilteredProducts(searchResults || []);
  };
  const fetchProductsData = async () => {
    try {
      setIsLoading(true);
      const res = await getCounterOrderProductsById(
        window.sessionStorage.getItem("counterCompanyLocationId") || selectedCounterData?.counter_company_location_id,
        "",
        "",
        selectedModule,  //M10-12866
        selectedCounterData?.counter_field_force_id?.[0]?.company_division_code?.company_divisioncode
      );
      dispatch(ordActions.setProductsData(res?.data?.data));
      res?.data?.data?.map((product: any) => {
        if (product?.counter_ff_div_id?.cmp_countertag?.is_division_order_required == true) {
          window.sessionStorage.setItem("iSalesRequired", product?.counter_ff_div_id?.cmp_countertag?.is_division_order_required);
        }
      });
      setIsLoading(false);
    } catch (error) {
      console.log(error);
      setIsLoading(false);
    }
  };

  const handleManualQuantityChange = (item: any, value: string) => {
    const counter_product_lnk_id = item?.counter_product_lnk_id;
    const numValue = Number(value);

    if (isNaN(numValue)) {
      return;
    }

    dispatch(
      ordActions.setCartDataObj({
        key: counter_product_lnk_id,
        value: {
          ...item,
          cartQty: numValue,
        },
      })
    );
  };

  // M10-12907: validates/corrects a manually typed quantity once the user leaves the field
  const handleQuantityBlur = (item: any) => {
    const counter_product_lnk_id = item?.counter_product_lnk_id;
    const currentProd = cartDataObj?.[counter_product_lnk_id];
    if (!currentProd) return;

    const { step, paidMaxQty, paidMinQty } = getSchemeStepAndMax(currentProd);
    const numValue = Number(currentProd?.cartQty);

    if (isNaN(numValue) || numValue < paidMinQty) {
      toast({ description: `You can add products from ${paidMinQty}`, variant: "destructive" });
      dispatch(ordActions.setCartDataObj({ key: counter_product_lnk_id, value: { ...currentProd, cartQty: paidMinQty } }));
      return;
    }

    if (numValue > paidMaxQty) {
      toast({ description: `Maximum order quantity is ${paidMaxQty}`, variant: "destructive" });
      dispatch(ordActions.setCartDataObj({ key: counter_product_lnk_id, value: { ...currentProd, cartQty: paidMaxQty } }));
      return;
    }

    if (step > 1 && (numValue - paidMinQty) % step !== 0) {
      const roundedQty = Math.min(Math.max(Math.round((numValue - paidMinQty) / step) * step + paidMinQty, paidMinQty), paidMaxQty);
      toast({ description: `You can add products from ${paidMinQty} in multiples of ${step}`, variant: "destructive" });
      dispatch(ordActions.setCartDataObj({ key: counter_product_lnk_id, value: { ...currentProd, cartQty: roundedQty } }));
    }
  };

  const fetchCounterData = async (counter_company_location_id: number) => {
    const query = `?counter_company_location_id=${counter_company_location_id}`;

    try {
      const response = await getCounterByEmployee(query);
      dispatch(ordActions.setSelectedCounterData(response?.data?.data?.[0]));
    } catch (err) {
      console.log(err);
    }
  };

  useEffect(() => {
    if (!productsData || productsData?.length === 0) {
      fetchProductsData();
    }
    if (!selectedCounterData || selectedCounterData.length === 0) {
      fetchCounterData(Number(window.sessionStorage.getItem("counterCompanyLocationId")));
    }
  }, []);

  const displayProducts = searchTerm ? filteredProducts : productsData;

  return (
    <div>
      <div>
        {isLoading ? (
          <div className="flex items-center justify-center h-screen">
            <Spinner />
          </div>
        ) : (
          <>
            <div className="p-6 px-0 border-b shadow-md">
              <Button className="text-lg px-16 text-[#7F56D9]" onClick={() => navigate(`/transactions/orders/details/${counterCode}/0`)}>
                <img src={PurpleLeftArrowIcon} alt="back" className="mr-4" />
                Back to Pharmacies
              </Button>
              <div className="w-full px-16">
                <h1 className="font-semibold text-[2rem]">{selectedCounterData?.counter_location?.counter_hdr_id?.counter_name}</h1>
                <div className="flex items-center justify-between gap-2 mt-2">
                  <div className="flex items-center gap-2">
                    <p className="font-semibold text-[.9rem] w-fit px-2 py-1 rounded-xl bg-[#EAF4FF] text-[#00527C]">
                      {selectedCounterData?.counter_location?.counter_hdr_id?.counter_code}
                    </p>
                    <p className="text-[#5E4200] w-fit px-2 py-1 rounded-xl font-semibold bg-[#FFF1E3] text-[.9rem]">
                      {selectedCounterData?.drph_lnk_type?.dr_ph_lnk_code}
                    </p>
                  </div>
                  <div className="flex items-center gap-2">
                    <Input placeholder="Search for products" value={searchTerm} onChange={handleSearchChange} className="h-12" />
                    <Button onClick={handleReviewOrder} className="bg-[#8051FF] text-white h-12 w-[12rem]">
                      Review Order
                    </Button>
                  </div>
                </div>{" "}
              </div>
            </div>
            {
              <div className="px-16 mt-6">
                {/* Header Section */}

                {/* Products Table */}
                <div className="p-6 bg-white rounded-lg shadow-sm">
                  <h2 className="mb-4 text-lg font-semibold">{searchTerm ? `Search Results (${displayProducts?.length})` : "Products"}</h2>
                  <div className="overflow-x-auto rounded-lg">
                    <table className="min-w-full border border-collapse border-gray-200 table-auto">
                      <thead className="bg-gray-100">
                        <tr>
                          <th className="px-6 py-3 text-sm font-medium text-left text-gray-600 border-b border-gray-200">Product Code</th>
                          <th className="px-6 py-3 text-sm font-medium text-left text-gray-600 border-b border-gray-200">Product Name</th>
                          <th className="px-6 py-3 text-sm font-medium text-left text-gray-600 border-b border-gray-200">Division</th>
                          <th className="px-6 py-3 text-sm font-medium text-left text-gray-600 border-b border-gray-200">Scheme</th>
                          <th className="px-6 py-3 text-sm font-medium text-left text-gray-600 border-b border-gray-200">Price (PTR)</th>
                          <th className="px-6 py-3 text-left text-sm font-medium text-gray-600 border-b border-gray-200">Period Type</th>
                          <th className="px-6 py-3 text-left text-sm font-medium text-gray-600 border-b border-gray-200">Period Limit</th>
                          <th className="px-6 py-3 text-sm font-medium text-left text-gray-600 border-b border-gray-200">Min Order Qty</th>
                          <th className="px-6 py-3 text-sm font-medium text-left text-gray-600 border-b border-gray-200">Max Order Qty</th>

                          <th className="px-6 py-3 w-[14rem] text-left text-sm font-medium text-gray-600 border-b border-gray-200">Quantity</th>
                        </tr>
                      </thead>
                      <tbody>
                        {displayProducts?.length === 0 ? (
                          <tr>
                            <td colSpan={6} className="py-6 text-center text-gray-500">
                              No products found matching your search.
                            </td>
                          </tr>
                        ) : (
                          displayProducts?.map((item: any) => {
                            const currentProd = cartDataObj?.[item?.counter_product_lnk_id];
                            const { step, paidMaxQty, paidMinQty } = getSchemeStepAndMax(currentProd || item);
                            const cartQty = Number(currentProd?.cartQty);
                            const isBelowMin = !!currentProd && cartQty < paidMinQty;
                            const isAboveMax = !!currentProd && cartQty > paidMaxQty;
                            const isNotStepAligned = !!currentProd && !isBelowMin && !isAboveMax && step > 1 && (cartQty - paidMinQty) % step !== 0;

                            return (
                            <tr key={item?.counter_product_lnk_id} className="border-b border-gray-200 hover:bg-gray-50">
                              <td className="px-6 py-4 text-sm text-gray-800">{item?.companyProducts?.item_code}</td>
                              <td className="px-6 py-4 text-sm text-gray-800">{item?.companyProducts?.name}</td>
                              <td className="px-6 py-4 text-sm text-gray-800">
                                {item?.counter_ff_div_id?.company_division_code?.division_name || "-"}
                              </td>
                              <td className="w-[14rem] px-6 py-6 flex justify-between text-sm text-gray-800">
                                {item?.priceldentifier?.description || "-"} :
                                {item?.priceldentifier?.priceldentifier_id == "2" && (
                                  <div className="rounded-md">
                                    <span className="px-2 py-1 text-sm text-gray-900 border border-gray-300 rounded-md">
                                      {item?.counter_product_slab?.[0]?.schemeqty_cp} + {item?.counter_product_slab?.[0]?.quantity_cp}
                                    </span>
                                  </div>
                                )}
                                {/* Fixed Price Section */}
                                {item?.priceldentifier?.priceldentifier_id == "3" && item?.counter_product_slab?.[0]?.fixedprice_cp && (
                                  <div className="rounded-md">
                                    <span className="px-2 py-1 text-sm text-gray-900 border border-gray-300 rounded-md">
                                      ₹{item?.counter_product_slab?.[0]?.fixedprice_cp}
                                    </span>
                                  </div>
                                )}
                                {/* Discount Section */}
                                {item?.priceldentifier?.priceldentifier_id == "1" && item?.counter_product_slab?.[0]?.discountonptr_cp && (
                                  <div className="rounded-md">
                                    <span className="px-2 py-1 text-sm text-gray-900 border border-gray-300 rounded-md">
                                      {item?.counter_product_slab?.[0]?.discountonptr_cp}%
                                    </span>
                                  </div>
                                )}
                              </td>
                              {/* <td className="px-6 py-4 text-sm text-gray-800">₹{item?.companyProducts?.ptr || "N/A"}</td> */}
                              <td className="px-6 py-4 text-sm text-gray-800">₹{item?.counter_product_slab?.[0]?.price}</td>
                              <td className="px-6 py-4 text-sm text-gray-800">{item?.periodtype}</td>
                              <td className="px-6 py-4 text-sm text-gray-800">{item?.periodlimit}</td>
                              <td className="px-6 py-4 text-sm text-gray-800">{item?.counter_product_slab?.[0]?.min_order_qty || "N/A"}</td>
                              <td className="px-6 py-4 text-sm text-gray-800">{item?.counter_product_slab?.[0]?.max_order_qty || "N/A"}</td>
                              <td className="px-6 py-4 text-sm text-gray-800">
                                {!cartDataObj?.[item?.counter_product_lnk_id] ? (
                                  <button
                                    onClick={() => handleAddToCart(item)}
                                    className="bg-[#8051FF] text-white px-4 py-2 rounded-lg hover:bg-[#6A42E4]"
                                  >
                                    <div className="flex items-center">
                                      <img src={cross} alt="cross" className="w-4 h-4 mr-2 rotate-45" />
                                      Add
                                    </div>
                                  </button>
                                ) : (
                                  <>
                                    <div className="flex items-center gap-2 w-fit">
                                      <button
                                        onClick={() => handleDecrement(item?.counter_product_lnk_id)}
                                        className="px-3 py-1 bg-white border border-gray-300 rounded-lg hover:bg-gray-100"
                                      >
                                        -
                                      </button>
                                      <Input
                                        isError={isBelowMin || isAboveMax || isNotStepAligned}
                                        enableArrows={false}
                                        showIcon={false}
                                        type="number"
                                        onChange={(e) => handleManualQuantityChange(item, e.target.value)}
                                        onBlur={() => handleQuantityBlur(item)}
                                        value={cartDataObj?.[item?.counter_product_lnk_id]?.cartQty}
                                        className="w-16 py-1 text-center border border-gray-300 rounded-lg"
                                      />
                                      <button
                                        onClick={() => handleIncrement(item?.counter_product_lnk_id)}
                                        className="px-3 py-1 bg-white border border-gray-300 rounded-lg hover:bg-gray-100"
                                      >
                                        +
                                      </button>
                                    </div>
                                    <p className="mt-1 ml-3 text-xs text-red-500">
                                      {isAboveMax && "Maximum order quantity is " + paidMaxQty}
                                      {isBelowMin && "You can add products from " + paidMinQty}
                                      {isNotStepAligned && `Please enter quantity from ${paidMinQty} in multiples of ${step}`}
                                    </p>
                                  </>
                                )}
                              </td>
                            </tr>
                            );
                          })
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            }
          </>
        )}
      </div>
    </div>
  );
};

export default OrdersProductScreen;
